import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CurrencyPipe, DatePipe, DOCUMENT, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, Inject, NgZone, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { Chat } from 'app/layout/common/quick-chat/quick-chat.types';
import { Subject, takeUntil } from 'rxjs';

import { DateTime } from 'luxon';
import { TFMService } from 'app/mock-api/tfm.service';

@Component({
    selector     : 'quick-chat',
    templateUrl  : './quick-chat.component.html',
    styleUrls    : ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs     : 'quickChat',
    standalone   : true,
    imports      : [NgClass, NgIf, MatIconModule, MatButtonModule, FuseScrollbarDirective, NgFor, NgTemplateOutlet, MatFormFieldModule, MatInputModule, TextFieldModule, DatePipe],
    providers    : [CurrencyPipe]
})
export class QuickChatComponent implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('messageInput') messageInput: ElementRef;

    view:any = {};

    chat: Chat;
    chats: Chat[];
    opened: boolean = false;
    selectedChat: Chat;
    private _mutationObserver: MutationObserver;
    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private _quickChatService: QuickChatService,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _tfmService: TFMService,
        private _currencyPipe: CurrencyPipe
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any
    {
        return {
            'quick-chat-opened': this.opened,
        };
    }

    /**
     * Resize on 'input' and 'ngModelChange' events
     *
     * @private
     */
    @HostListener('input')
    @HostListener('ngModelChange')
    private _resizeMessageInput(): void
    {
        // This doesn't need to trigger Angular's change detection by itself
        this._ngZone.runOutsideAngular(() =>
        {
            setTimeout(() =>
            {
                // Set the height to 'auto' so we can correctly read the scrollHeight
                this.messageInput.nativeElement.style.height = 'auto';

                // Get the scrollHeight and subtract the vertical padding
                this.messageInput.nativeElement.style.height = `${this.messageInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Chat
        this._quickChatService.chat$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chat: Chat) =>
            {
                this.chat = chat;
            });

        // Chats
        this._quickChatService.chats$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chats: Chat[]) =>
            {
                this.chats = chats;
                // this._tfmService.log(chats);
            });

        // Selected chat
        this._quickChatService.chat$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((chat: Chat) =>
            {
                this.selectedChat = chat;
            });
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void
    {
        if (typeof MutationObserver !== "undefined") {
            // Fix for Firefox.
            //
            // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
            // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
            // This fixes the problem by reading the 'top' value from the html element and adding it as a
            // 'marginTop' to the navigation itself.
            this._mutationObserver = new MutationObserver((mutations) =>
            {
                mutations.forEach((mutation) =>
                {
                    const mutationTarget = mutation.target as HTMLElement;
                    if ( mutation.attributeName === 'class' )
                    {
                        if ( mutationTarget.classList.contains('cdk-global-scrollblock') )
                        {
                            const top = parseInt(mutationTarget.style.top, 10);
                            this._renderer2.setStyle(this._elementRef.nativeElement, 'margin-top', `${Math.abs(top)}px`);
                        }
                        else
                        {
                            this._renderer2.setStyle(this._elementRef.nativeElement, 'margin-top', null);
                        }
                    }
                });
            });
            this._mutationObserver.observe(this._document.documentElement, {
                attributes     : true,
                attributeFilter: ['class'],
            });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        if (this._mutationObserver) {
            // Disconnect the mutation observer
            this._mutationObserver.disconnect();
        }

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    open(): void
    {
        // Return if the panel has already opened
        if ( this.opened )
        {
            return;
        }

        // Open the panel
        this._toggleOpened(true);
    }

    /**
     * Close the panel
     */
    close(): void
    {
        // Return if the panel has already closed
        if ( !this.opened )
        {
            return;
        }

        // Close the panel
        this._toggleOpened(false);
    }

    /**
     * Toggle the panel
     */
    toggle(): void
    {
        if ( this.opened )
        {
            this.close();
        }
        else
        {
            this.open();
        }
    }

    /**
     * Select the chat
     *
     * @param id
     */
    selectChat(id: string): void
    {
        // Open the panel
        this._toggleOpened(true);

        // Get the chat data
        this._quickChatService.getChatById(id).subscribe();
    }

    /**
     * Add message to the chat
     *
     * @param id
     */
    addMessage(message: string = null, isMine = true): void
    {
        message = message || this.messageInput.nativeElement.value;
        this.messageInput.nativeElement.value = "";

        var messageToSend = message;

        var previousMessages = [];
        if (isMine) { //Pass in the messages for the last 10minutes to keep context
            this.selectedChat.messages.filter(x => DateTime.fromISO(x.createdAt).diffNow("minutes").minutes >= -10 && DateTime.fromISO(x.createdAt).diffNow("minutes").minutes <= 0).forEach(x => {
                previousMessages.push({
                    role: x.isMine ? "user" : "assistant",
                    content: x.value
                });
            });

            this._tfmService.log(previousMessages);
        }

        if (this.selectedChat && this.selectedChat.messages) {
            this.selectedChat.messages.push({
                chatId: this.selectedChat.id,
                contactId: this.selectedChat.contactId,
                createdAt: new Date().toISOString(), //"2023-11-26T18:56:58.169+02:00",
                id: "e6b2b82f-b199-4a60-9696-5f3e40d2715d",
                isMine: isMine,
                value: message
            });

            if (isMine) {
                //Send to AI bot
                this._quickChatService.sendMessageToAI(messageToSend, previousMessages).subscribe(res => {
                    this._tfmService.log(res);
                    if (res.choices && res.choices.length > 0) {
                        var responseText = res.choices[0].message.content;

                        //Try extract JSON
                        var jsonStartIndex = responseText.indexOf("{");
                        var jsonEndIndex = responseText.indexOf("}");
                        if (jsonStartIndex >= 0 && jsonEndIndex > jsonStartIndex) { //We have JSON
                            var jsonIntent = responseText.substring(jsonStartIndex, jsonStartIndex + (jsonEndIndex - jsonStartIndex) + 1);
                            this._tfmService.log(jsonIntent);

                            var jsonIntentObj = JSON.parse(jsonIntent);
                            this._tfmService.log(jsonIntentObj);

                            this.addMessage("Give me a moment while I get you the details...", false);

                            //Let's execute the actual intent search
                            this.searchHotels(jsonIntentObj.destination, jsonIntentObj.checkIn, jsonIntentObj.checkOut, jsonIntentObj.adults || 2, jsonIntentObj.teens || 0, jsonIntentObj.children || 0, jsonIntentObj.infants || 0, previousMessages);
                        }
                        else { //Only add the response if we do not have JSON - in this case we don't have any
                            this.addMessage(responseText, false);
                        }
                    }
                });
            }
        }
    }
    searchHotels(destination, dateFrom, dateTo, adults, teens, children, infants, previousMessages = []) {
        this._tfmService.getPlaces(destination, "any").subscribe(resPlaces => {
            this._tfmService.log(resPlaces);

            if (resPlaces.content.predictions && resPlaces.content.predictions.length > 0) {
                var destinationPlaceID = resPlaces.content.predictions[0].place_id;
                var destinationName = resPlaces.content.predictions[0].description;

                this.addMessage(`Checking ${destinationName}...`, false);

                var searchContentAccommodation = {
                    "googlePlaceID": destinationPlaceID,
                    "location": destinationName,
                    "checkInDate": new Date(dateFrom),
                    "checkOutDate": new Date(dateTo),
                    "roomCount": 1,
                    occupancy: this.view.search.occupancy,
                    // "occupancy": [{
                    //     "adultCount": adults,
                    //     "teenCount": teens,
                    //     "childCount": children,
                    //     "infantCount": infants,
                    //     "childAges": this.view.search.pax.childAges
                    // }],
                    "agentName": this.view.agentName,
                    "siteSearchProviders": "207,55,210,17,85,18,34,19,31"
                    // "searchFilter": {
                    //   "accommodation": {
                    //     gradingMin:4,
                    //     gradingMax:4
                    //   }
                    // }
                    // "occupancy": [{
                    //     "adultCount":this.trip.pax.adults,
                    //     "childCount":this.trip.pax.children,
                    //     "infantCount":this.trip.pax.infants
                    // }]
                };
            
                this._tfmService.saveSearch(searchContentAccommodation, 8).subscribe(search => {
                    //this.view.progress.search.hotel.percentage = 25;
            
                    this._tfmService.send({ href: search.url, method: "get" }).subscribe(searchResult => {
                    //this.view.progress.search.hotel.percentage = 50;
            
                    this._tfmService.filterAndSend(searchResult.content._links, "search-results-accommodation-paged").subscribe(searchResultAccommodation => {
                        //this.view.progress.search.hotel.percentage = 75;
            
                        this._tfmService.log("searchResultAccommodation", searchResultAccommodation);

                        if (searchResultAccommodation.data && searchResultAccommodation.data.length > 0 && searchResultAccommodation.data[0].results && searchResultAccommodation.data[0].results.roomStays) {
                            var searchResultText = "";

                            searchResultAccommodation.data[0].results.roomStays.roomStay.forEach(rs => {
                                searchResultText += `${rs.basicPropertyInfo.hotelName} a ${rs.basicPropertyInfo.userGeneratedContent.rating} star hotel from only ${this._currencyPipe.transform(rs.total.amountBeforeTax, rs.total.currencyCode, 'symbol-narrow')}. ` ;
                            });

                            //Rewrite the response in a more exciting way
                            previousMessages.push({
                                role: "system",
                                content: "Rewrite this in a more exciting way in point form with line spaces to try and convert the user: " + searchResultText
                            });
                            
                            this._quickChatService.sendMessageToAI("ignore", previousMessages).subscribe(res => {
                                this._tfmService.log(res);
                                if (res.choices && res.choices.length > 0) {
                                    var responseText = res.choices[0].message.content;

                                    responseText = responseText.replaceAll("\n", "<br/>");
                                    this.addMessage(responseText, false);
                                }
                            });
                        }

                        //this.processSearchResultAccommodation(searchResultAccommodation, point);
            
                        //this.view.data.hotel = searchResultAccommodation;
                        //this.processHotels(searchResultAccommodation, point.index);
            
                        //this.view.progress.search.hotel.percentage = 100;
                        //this.tfmService.saveLocalStorage("dataHotel", this.view.data.hotel);
                    });
                    });
                });
            }
        });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the backdrop
     *
     * @private
     */
    private _showOverlay(): void
    {
        // Try hiding the overlay in case there is one already opened
        this._hideOverlay();

        // Create the backdrop element
        this._overlay = this._renderer2.createElement('div');

        // Return if overlay couldn't be create for some reason
        if ( !this._overlay )
        {
            return;
        }

        // Add a class to the backdrop element
        this._overlay.classList.add('quick-chat-overlay');

        // Append the backdrop to the parent of the panel
        this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Add an event listener to the overlay
        this._overlay.addEventListener('click', () =>
        {
            this.close();
        });
    }

    /**
     * Hide the backdrop
     *
     * @private
     */
    private _hideOverlay(): void
    {
        if ( !this._overlay )
        {
            return;
        }

        // If the backdrop still exists...
        if ( this._overlay )
        {
            // Remove the backdrop
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }

        // Disable block scroll strategy
        this._scrollStrategy.disable();
    }

    /**
     * Open/close the panel
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void
    {
        // Set the opened
        this.opened = open;

        // If the panel opens, show the overlay
        if ( open )
        {
            this._showOverlay();
        }
        // Otherwise, hide the overlay
        else
        {
            this._hideOverlay();
        }
    }
}
